/**
 * ModalContext - Provides modal control functions
 * 
 * This context provides functions for triggering various modals,
 * separate from other operations to minimize re-renders.
 */

import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

// Create the context
const ModalContext = createContext({
    showMealDetails: () => {},
    startRepeatMeal: () => {},
    showUpgradeForm: () => {},
    showGroceries: () => {},
    showNutrition: () => {},
    showProfile: () => {},
    editMealBatches: () => {},
    editRecipe: () => {},
    editFood: () => {},
    recommendRecipe: () => {},
    recommendPlan: () => {},
    getModalData: () => null,
});

// Custom hook for consuming modal controls
export const useModalControls = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModalControls must be used within ModalProvider');
    }
    return context;
};

// Provider component
export const ModalProvider = ({ 
    children,
    showMealDetails,
    startRepeatMeal,
    showUpgradeForm,
    showGroceries,
    showNutrition,
    showProfile,
    editMealBatches,
    editRecipe,
    editFood,
    recommendRecipe,
    recommendPlan,
    getModalData,
}) => {
    // Functions are stable references, no need for useMemo
    const value = {
        showMealDetails,
        startRepeatMeal,
            showUpgradeForm,
        showGroceries,
        showNutrition,
        showProfile,
        editMealBatches,
        editRecipe,
        editFood,
        recommendRecipe,
        recommendPlan,
        getModalData,
    };
    
    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};

ModalProvider.propTypes = {
    children: PropTypes.node.isRequired,
    showMealDetails: PropTypes.func,
    startRepeatMeal: PropTypes.func,
    showUpgradeForm: PropTypes.func,
    showGroceries: PropTypes.func,
    showNutrition: PropTypes.func,
    showProfile: PropTypes.func,
    editMealBatches: PropTypes.func,
    editRecipe: PropTypes.func,
    editFood: PropTypes.func,
    recommendRecipe: PropTypes.func,
    recommendPlan: PropTypes.func,
    getModalData: PropTypes.func,
};

export default ModalContext;